import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { OnboardingStatus } from "@ferrivia/api-contract";
import { useApi, useSession } from "@ferrivia/auth-web";
import { markBootReady } from "../boot/bootSplash";
import { readCachedSession, writeCachedSession } from "../offline/cachedSession";
import { OnboardingFlow } from "./OnboardingFlow";

type GateState =
  | { status: "loading" }
  | { status: "onboarding"; initial: OnboardingStatus }
  | { status: "done" };

export interface OnboardingGateProps {
  children: ReactNode;
}

/**
 * The hard gate. Renders the flow instead of the app until the client has
 * completed onboarding, and sits outside BrowserRouter — TabPanels bounces
 * any path not in TABS back to "/", so an onboarding *route* would need a
 * non-tab route concept, whereas a gate screen is the shape AuthGate already
 * uses for passkey enrolment. The URL is left untouched either way.
 *
 * Ordering: passkey enrolment first (it lives inside AuthGate), then this.
 * Enrolment is skippable per device and must not be stranded behind a form.
 *
 * This is not the enforcement, only the presentation of it — the API refuses
 * client-data routes to an un-onboarded caller regardless of what this
 * component renders. See requireOnboarded in the api's auth guards.
 */
export function OnboardingGate({ children }: OnboardingGateProps) {
  const api = useApi();
  const session = useSession();
  // A cache-hydrated boot already knows whether onboarding was complete last
  // time it was verified — seeding the gate open with it means a
  // cache-hydrated AppShell doesn't sit blank behind a second network round
  // trip. `load` below still runs and corrects this once the real GET lands.
  const [state, setState] = useState<GateState>(() =>
    readCachedSession()?.onboardingComplete ? { status: "done" } : { status: "loading" },
  );

  const load = useCallback(async () => {
    try {
      const status = await api.onboarding.get();
      setState(status.status === "complete" ? { status: "done" } : { status: "onboarding", initial: status });
    } catch {
      // A failed status read must not lock a client out of an app they may
      // well have already onboarded into: fall open here and let the API's
      // own guard be the thing that says no. The alternative — a blank
      // screen on a flaky network — is worse and lies about the cause.
      setState({ status: "done" });
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (state.status === "onboarding") {
      markBootReady("onboarding");
    }
  }, [state.status]);

  // Refresh the cached snapshot whenever a verified session settles into a
  // known onboarding state — trust "verified" only, so a cache-hydrated
  // shell never re-writes the cache from its own seed data.
  useEffect(() => {
    if (session.trust !== "verified" || !session.user) return;
    if (state.status === "done") {
      writeCachedSession(session.user, true);
    } else if (state.status === "onboarding") {
      writeCachedSession(session.user, false);
    }
  }, [session.trust, session.user, state.status]);

  if (state.status === "loading") {
    // Same as AuthGate's own loading branch: nothing, rather than a spinner
    // that flashes on every cold start.
    return null;
  }

  if (state.status === "onboarding") {
    return <OnboardingFlow initial={state.initial} onDone={() => setState({ status: "done" })} />;
  }

  return <>{children}</>;
}
